import { useMemo } from 'react'
import { Variant } from '@phrasee/phrasee-typings/typings/futurama/element'

import { CellProps, Column, OnCellChange } from 'common/components/table'
import useTableActions from 'common/hooks/useTableActions'
import { replaceNewLineEmojiAsString } from 'common/variantsUtils'

import RecommendedCell from '../../RecommendedCell'
import { ActionCells } from '../ActionsCell'
import { ExtendedVariant } from '../ContentTable'

type ContentTableColumnsProps = {
  handleMoreLikeThisVariants: (variantId: number) => void
  handleReplaceVariants: (variantId: number) => void
  handleDeleteVariants: (variant: Variant) => void
  onEditClick: (variant: Variant) => void
}

export const useContentTableColumns = ({
  handleMoreLikeThisVariants,
  handleReplaceVariants,
  handleDeleteVariants,
  onEditClick,
}: ContentTableColumnsProps) => {
  const { onCopy } = useTableActions()

  return useMemo(
    () =>
      [
        {
          accessor: 'text',
          disableSortBy: true,
          disableFilters: true,
          Cell: (
            props: CellProps<Variant & { recommended: boolean }> & {
              onCellChange: OnCellChange<Variant>
            }
          ) => {
            return (
              <div className="w-full flex items-center py-4 pl-2 min-h-14 whitespace-pre-wrap">
                {replaceNewLineEmojiAsString(props.value)}
                <RecommendedCell
                  isRecommended={props.row.original.recommended}
                />
              </div>
            )
          },
        },
        {
          Header: 'Actions',
          id: 'actions',
          accessor: 'variant_id',
          disableSortBy: true,
          align: 'right',
          disableFilters: true,
          width: 160,
          className: 'flex-none',
          Cell: ({ row }) => (
            <ActionCells
              onMoreLikeThis={() =>
                handleMoreLikeThisVariants(row.original.variant_id)
              }
              isMoreLikeThisEnabled={row.depth === 0}
              onCopy={() => onCopy(row.values.text)}
              onRefresh={() => handleReplaceVariants(row.original.variant_id)}
              onDelete={() => handleDeleteVariants(row.original)}
              onEdit={() => onEditClick(row.original)}
              isDisabled={row.original.isDisabled}
              isRefreshing={row.original.isRefreshing || false}
              isRefreshSuccessful={row.original.isRefreshSuccessful}
              isMoreLikeThisSuccessful={row.original.isMoreLikeThisSuccessful}
              isLoadingMoreLikeThis={
                row.original.isLoadingMoreLikeThis || false
              }
            />
          ),
        },
      ] as Column<ExtendedVariant & { recommended: boolean }>[],
    [
      handleMoreLikeThisVariants,
      onCopy,
      handleReplaceVariants,
      handleDeleteVariants,
      onEditClick,
    ]
  )
}
